import { useCallback } from 'react';
import type { Report, Company, Site, ReportView } from '@/data/schemas';
import type { AppSettings } from '@/data/settings/defaultSettings';
import { generateDocumentDefinition } from '../components/PDFMakeRenderer';
import { augmentReportForRendering } from '../utils/augmentReportForRendering';
import type { PdfWorkerRequest, PdfWorkerResponse } from '../workers/pdfmake.worker';
import { trackReportPdfGenerated } from '@/lib/metrics';
// Worker singleton - reused across PDF generations
let pdfWorker: Worker | null = null;

function getPdfWorker(): Worker {
  if (!pdfWorker) {
    pdfWorker = new Worker(new URL('../workers/pdfmake.worker.ts', import.meta.url), {
      type: 'module',
    });
  }
  return pdfWorker;
}

/**
 * Generate PDF blob using Web Worker to keep main thread responsive.
 * This prevents Chrome's "Page Unresponsive" dialog on slow machines.
 *
 * The view data is serializable JSON. Document definition generation
 * happens inside the worker because it contains callback functions
 * that can't be cloned for postMessage.
 */
async function generatePdfBlobViaWorker(
  view: ReportView,
  options: { isDraft?: boolean; showChanges?: boolean; fileName?: string }
): Promise<Blob> {
  const worker = getPdfWorker();
  const TIMEOUT_MS = 60000; // 60 second timeout

  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      worker.removeEventListener('message', handleMessage);
      worker.removeEventListener('error', handleError);
      reject(new Error('Generazione PDF scaduta dopo 60 secondi'));
    }, TIMEOUT_MS);

    const handleMessage = (event: MessageEvent<PdfWorkerResponse>) => {
      clearTimeout(timeoutId);
      worker.removeEventListener('message', handleMessage);
      worker.removeEventListener('error', handleError);

      if (event.data.type === 'success' && event.data.blob) {
        resolve(event.data.blob);
      } else {
        reject(new Error(event.data.error ?? 'PDF generation failed'));
      }
    };

    const handleError = (error: ErrorEvent) => {
      clearTimeout(timeoutId);
      worker.removeEventListener('message', handleMessage);
      worker.removeEventListener('error', handleError);
      reject(new Error(error.message ?? 'Worker error'));
    };

    worker.addEventListener('message', handleMessage);
    worker.addEventListener('error', handleError);

    worker.postMessage({
      type: 'generate',
      view,
      isDraft: options.isDraft,
      showChanges: options.showChanges,
      fileName: options.fileName,
    } satisfies PdfWorkerRequest);
  });
}

type PdfPreviewResult =
  | { status: 'opened' }
  | { status: 'blocked' }
  | { status: 'downloaded_fallback'; fileName: string }
  | { status: 'unsupported' };

interface PdfDownloadResult {
  fileName: string;
}

interface UseUnifiedPdfExportOptions {
  report: Report | null;
  company: Company | null;
  site?: Site | null;
  settings?: AppSettings;
  showChanges?: boolean;
  isDraft?: boolean;
  fileName?: string;
}

/**
 * PDF export hook using ReportView architecture
 */
export function useUnifiedPDFExport({
  report,
  company,
  site,
  settings,
  showChanges = false,
  isDraft = true,
  fileName,
}: UseUnifiedPdfExportOptions) {
  const getFileName = useCallback(() => {
    if (fileName) {
      return toPdfFileName(fileName);
    }

    if (!report) {
      return 'report.pdf';
    }

    const title = sanitizeFileName(report.meta.title || 'report');
    const datePart = sanitizeDatePart(
      report.meta.visitDate ?? report.updatedAt ?? new Date().toISOString()
    );

    return `${title}-${datePart}.pdf`;
  }, [fileName, report]);

  const buildView = useCallback(async (): Promise<ReportView> => {
    if (!report || !company) {
      throw new Error("Impossibile generare il PDF senza i dati del report e dell'azienda");
    }

    const { view } = await augmentReportForRendering(report, company, site ?? null, settings);

    return view;
  }, [report, company, site, settings]);

  // Keep buildDefinition for backward compatibility (used by some callers)
  const buildDefinition = useCallback(async () => {
    const view = await buildView();
    return generateDocumentDefinition({
      view,
      isDraft,
      showChanges,
      fileName: getFileName(),
    });
  }, [buildView, isDraft, showChanges, getFileName]);

  const generatePdf = useCallback(async (): Promise<PdfDownloadResult> => {
    const view = await buildView();
    const fileName = getFileName();

    // Generate PDF in worker to keep main thread responsive
    const pdfBlob = await generatePdfBlobViaWorker(view, {
      isDraft,
      showChanges,
      fileName,
    });

    // Trigger download
    const pdfUrl = URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(pdfUrl);
    trackReportPdfGenerated();

    return { fileName } satisfies PdfDownloadResult;
  }, [buildView, getFileName, isDraft, showChanges]);

  const previewPdf = useCallback(async (): Promise<PdfPreviewResult> => {
    if (typeof window === 'undefined') {
      await buildView();
      return { status: 'unsupported' };
    }

    const fileName = getFileName();
    try {
      const view = await buildView();

      // Generate PDF in worker to keep main thread responsive
      const pdfBlob = await generatePdfBlobViaWorker(view, {
        isDraft,
        showChanges,
        fileName,
      });

      const pdfUrl = URL.createObjectURL(pdfBlob);
      const previewWindow = window.open(pdfUrl, '_blank', 'noopener,noreferrer');

      if (!previewWindow) {
        // Popup was blocked - fall back to download
        const link = document.createElement('a');
        link.href = pdfUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(pdfUrl);
        trackReportPdfGenerated();
        return { status: 'downloaded_fallback', fileName };
      }

      previewWindow.document.title = `Anteprima PDF - ${fileName}`;
      previewWindow.focus();
      previewWindow.addEventListener('beforeunload', () => URL.revokeObjectURL(pdfUrl), {
        once: true,
      });
      trackReportPdfGenerated();

      return { status: 'opened' };
    } catch (error) {
      throw error;
    }
  }, [buildView, getFileName, isDraft, showChanges]);

  return {
    generatePdf,
    previewPdf,
    buildDefinition,
  };
}

function sanitizeFileName(value: string): string {
  const normalized = value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[<>:"/\\|?*]+/g, ' ')
    .trim()
    .toLowerCase();

  const slug = normalized.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return slug.length > 0 ? slug : 'report';
}

function toPdfFileName(value: string): string {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return 'report.pdf';
  }

  const hasExtension = trimmed.toLowerCase().endsWith('.pdf');
  const base = hasExtension ? trimmed.slice(0, -4) : trimmed;
  const sanitizedBase = sanitizeFileName(base);
  return `${sanitizedBase}.pdf`;
}

function sanitizeDatePart(value: string): string {
  const digits = value.replace(/[^0-9]/g, '');
  if (digits.length >= 8) {
    return digits.slice(0, 8);
  }
  return (digits + '00000000').slice(0, 8);
}
